import { useState } from 'react'
import LazyLoad from 'react-lazyload'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import styled from '@emotion/styled'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import Link from 'components/common/A'
import BreakWordTypography from 'components/common/BreakWordTypography'
import { useImageLoaded } from 'utils/hooks'

const minHeightValue = '0px'

const AbsoluteTypography = styled(Box)`
  ${({ theme }) => `
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: flex-end;
    padding: 16px;

    ${theme.breakpoints.up('md')} {
      justify-content: center;
      padding: 24px 16px;
    }

    ${theme.breakpoints.up('lg')} {
      &:hover {
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
    }
  `}
`

const ServiceCardBox = styled(Box)`
  ${({ theme }) => `
    position: relative;
    cursor: pointer;
    background-color: transparent;
    border-radius: 12px;
    overflow: hidden;
    -webkit-transition: all 0.2s ease-in-out;
    transition: all 0.2s ease-in-out;


    ${theme.breakpoints.up('sm')} {
      border-radius: 24px;
    }

    ${theme.breakpoints.up('md')} {
      width: 164px;
      height: 164px;
      padding-top: 0;
    }

    ${theme.breakpoints.up('lg')} {
      width: 140px;
      height: 140px;

      &:hover {
        box-shadow: 0px 14px 12px rgba(0, 0, 0, 10%);
      }
    }

    ${theme.breakpoints.up('xl')} {
      width: 164px;
      height: 164px;  
    }

    ${theme.breakpoints.up('xxl')} {
      width: 218px;
      height: 218px;
    }
  `}
`

const ImgMask = styled.div`
  ${({ theme }) => `
    position: absolute;
    top: 40%;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    border-radius: 12px;
    background-image: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.4));

    ${theme.breakpoints.up('sm')} {
      border-radius: 24px;    
    }
  `}
`

const ImgHoverMask = styled.div`
  ${({ theme }) => `
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    top: 10%;
    border-radius: 12px;
    background-image: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.6));

    ${theme.breakpoints.up('sm')} {
      border-radius: 24px;    
    }
  `}
`

const ServiceCardDEFAULT = styled.div`
  ${({ theme }) => `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    background-color: ${theme.palette.primary.minus2};
    border-radius: 12px;

    ${theme.breakpoints.up('sm')} {
      border-radius: 24px;
    }
  `}
`

const ServiceCardImg = styled.img`
  ${({ theme }) => `
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: bottom;
    border-radius: 12px;

    ${theme.breakpoints.up('sm')} {
      border-radius: 24px;
    }
  `}
`

const ImgRect = styled(Box)`
  ${({ theme }) => `
    position: relative;
    padding-top: 66.6%;

    ${theme.breakpoints.up('md')} {
      padding-top: 100%;
    }
  `}
`

const Absolute = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

const ServiceCard = ({ catalogData, lazy, lazyOptions }) => {
  const [hovered, setHovered] = useState(false)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const [ref, isImageLoaded, onLoadComplete] = useImageLoaded()

  return (
    <LazyLoad
      height={minHeightValue}
      offset={lazyOptions?.offset ?? 0}
      scrollContainer={
        lazyOptions?.containerId && `#${lazyOptions.containerId}`
      }
    >
      <ServiceCardBox
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {!isImageLoaded ? (
          <ServiceCardDEFAULT />
        ) : isDesktop && hovered ? (
          <ImgHoverMask />
        ) : (
          <ImgMask />
        )}
        <Link href={catalogData.link ? catalogData.link : ''} $origin>
          <ImgRect>
            <Absolute>
              {isDesktop ? (
                <ServiceCardImg
                  ref={ref}
                  onLoad={onLoadComplete}
                  src={catalogData.displayBelowBannerInfo?.image?.url}
                />
              ) : (
                <ServiceCardImg
                  ref={ref}
                  onLoad={onLoadComplete}
                  src={catalogData.displayBelowBannerInfo?.mobileImage?.url}
                />
              )}
              <AbsoluteTypography
                variant="subtitle2"
                color="primary.contrastText"
              >
                <Typography
                  variant="subtitle2"
                  color="primary.contrastText"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {catalogData.name}
                </Typography>
                {isDesktop && hovered && (
                  <BreakWordTypography
                    variant={{ xs: 'body2', xl: 'label' }}
                    color="primary.contrastText"
                    sx={{ textAlign: 'center' }}
                    mt={1}
                  >
                    {catalogData.displayBelowBannerInfo?.description}
                  </BreakWordTypography>
                )}
              </AbsoluteTypography>
            </Absolute>
          </ImgRect>
        </Link>
      </ServiceCardBox>
    </LazyLoad>
  )
}

ServiceCard.defaultProps = {
  lazy: false,
  lazyOptions: { offset: 0, containerId: '' },
}

export default ServiceCard
